import React from 'react'
import { useTable } from 'react-table'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { SmHeadline } from '../styles/SharedStyles'
import styled from 'styled-components'
import { MediaQueries as Mq } from '../styles/MediaQueries'

const StyledTh = styled.th`
  padding: 1.25rem;
  //border-bottom: 1px solid var(--lightGray);
  background: var(--paleBlue);
  color: var(--baseBlue);
  font-weight: bold;
  text-align: left;
  max-width: 0; // this makes the column take up only as much space as the header
  min-width: 100px;
  word-wrap: normal;
  :nth-child(1) {
    border-right: 1px solid var(--lightGray);
    padding: 1.25rem;
    min-width: 175px;
  }
  :last-child {
    padding-right: 1.25rem;
  }
  &.custom {
    padding: 0.5rem 1.25rem 1.25rem 1.25rem;
    :nth-child(1) {
      border-right: none;
    }
    :nth-child(2) {
      border-right: 1px solid var(--lightGray);
    }
  }
`
const ExtraTh = styled.th`
  //padding: 1rem 1.25rem 0.25rem 1.25rem;
  background: var(--paleBlue);
  color: var(--baseBlue);
  font-weight: bold;
  text-align: left;
  border-bottom: none;
  :nth-child(1) {
    border-right: 1px solid var(--lightGray);
  }
  &.custom {
    padding: 1rem 1.25rem 0.25rem 1.25rem;
  }
`
const StyledTr = styled.tr`
  :nth-child(1) {
    font-weight: bold;
    border-bottom: 1px solid var(--lightGray);
  }
  &.regular {
    font-weight: normal;
    border-bottom: none;
  }
  &.ig12 {
    border-top: 1px solid var(--lightGray);
  }
  &.sp61 {
    border-bottom: 1px solid var(--lightGray);
  }
`
const StyledTd = styled.td`
  padding: 1rem;
  border-bottom: none;

  :nth-child(1) {
    border-right: 1px solid var(--lightGray);
    padding: 1.25rem;
    width: 30%;
  }
  &.ig12 {
    :nth-child(1) {
      border-right: none;
      padding: 1rem;
      max-width: 25%;
    }
    :nth-child(2) {
      border-right: 1px solid var(--lightGray);
      padding: 1rem;
      //just needs this for max-width to work:
      width: 100%;
      max-width: 25%;
    }
  }
  &.sp61 {
    font-weight: bold;
  }
`
const PillForSp61 = styled.div`
  background-color: ${({ bgColor }) => `var(--${bgColor})`};
  font-size: 1.15rem;
  font-weight: bold;
  color: ${({ color }) => (color ? `var(--${color})` : 'transparent')};
  padding: 0.5rem;
  border-radius: 50px;
  width: 9rem;
  text-align: center;
  sup {
    font-size: 60%;
    top: -0.6em;
    color: ${({ color }) => (color ? `var(--${color})` : 'var(--darkGray)')};
  }
`

const StyledTable = styled.table`
  width: 100%;
  margin-top: 1rem;
  font-size: 1.25rem;
  ${Mq.sm} {
    font-size: 1.1rem;
  }
`
const Container = styled.div`
  overflow-x: scroll;
`
const Table = ({
  data,
  columns,
  noBoldTopRow,
  ig,
  sp,
  description,
  numYearCols
}) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data })
  const { t } = useTranslation()

  const COLORS_SP61 = {
    Unhealthy: 'darkOrange',
    Concerning: 'mediumOrange',
    Healthy: 'brightBlue'
  }

  const TEXT_COLORS_SP61 = {
    Unhealthy: 'white',
    Concerning: 'darkGray',
    Healthy: 'white',
    'Not applicable': 'black'
  }

  //Customized for IG1.2 and SP6.1
  return (
    <>
      {sp !== '61' && (
        <SmHeadline color={'baseBlue'}>{t('summary table')}</SmHeadline>
      )}
      <Container>
        <StyledTable {...getTableProps()} title={description}>
          <thead>
            {ig === '12' && (
              <tr>
                <>
                  <ExtraTh colSpan={2}></ExtraTh>
                  <ExtraTh className="custom" colSpan={numYearCols}>
                    {t(
                      'no. of countries achieving target out of those with target'
                    )}
                  </ExtraTh>
                </>
              </tr>
            )}
            {sp === '51' && (
              <tr>
                <>
                  <ExtraTh colSpan={1}></ExtraTh>
                  <ExtraTh className="custom" colSpan={numYearCols}>
                    {t('outbreaks with timely detection and response')}
                  </ExtraTh>
                </>
              </tr>
            )}
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <StyledTh
                    {...column.getHeaderProps()}
                    className={ig === '12' ? 'custom' : null}
                  >
                    {column.render('Header')}
                  </StyledTh>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row)
              // For IG1.2, if "type" is not blank, it's the beginning of a new category and needs a line at the top
              // console.log('row', row)
              return (
                <StyledTr
                  {...row.getRowProps()}
                  className={`${noBoldTopRow && 'regular'} ${
                    ig === '12' && row.values.type !== '' && 'ig12'
                  } ${sp === '61' && 'sp61'}`}
                >
                  {row.cells.map((cell) => {
                    // console.log('cell', cell.value)
                    return (
                      <StyledTd
                        {...cell.getCellProps()}
                        className={`${ig === '12' && 'ig12'} ${
                          sp === '61' &&
                          cell.column.Header === t('vaccine') &&
                          'sp61'
                        }`}
                      >
                        {sp === '61' ? (
                          cell.column.Header !== t('vaccine') ? (
                            cell.value ? (
                              <PillForSp61
                                bgColor={COLORS_SP61[cell.value]}
                                color={TEXT_COLORS_SP61[cell.value]}
                              >
                                {t(cell.value)}
                                {/* {cell.value.status
                                  ? cell.value.status
                                  : //acts as placeholder but will be transparent:
                                    'no grade'} */}
                                {/* <sup
                                  color={TEXT_COLORS_SP61[cell.value.status]}
                                >
                                  {cell.value.note}
                                </sup> */}
                              </PillForSp61>
                            ) : (
                              <PillForSp61
                                color={TEXT_COLORS_SP61['Not applicable']}
                              >
                                {t('Not applicable')}
                              </PillForSp61>
                            )
                          ) : (
                            <>
                              {cell.value.name}
                              <sup>{cell.value.note}</sup>
                            </>
                          )
                        ) : (
                          cell.render('Cell')
                        )}
                      </StyledTd>
                    )
                  })}
                </StyledTr>
              )
            })}
          </tbody>
        </StyledTable>
      </Container>
    </>
  )
}

export default Table
